import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-responsive-modal';
import { showConfirmAlert, showErrorAlert, showSuccessAlert, showConfirmAlertWithCheckbox } from '../utils/alerts';
import { updateUser, deactivateUser, activateUser } from '../utils/services/userService';

const UsersList = ({ users = [], onUserSave }) => {
  const [rowInEditMode, setRowInEditMode] = useState(null);
  const [valuesInEditMode, setValuesInEditMode] = useState({});
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const openPhotoModal = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
    setShowPhotoModal(false);
  };

  const enterEditMode = (id) => {
    try {
      setRowInEditMode(id);
      const userData = users.find((item) => item.user.id === id);
      if (!userData) {
        showErrorAlert('Error', `No se encontró el usuario seleccionado.`);
        return;
      }

      setValuesInEditMode(userData.user);
    } catch (error) {
      console.error('Error', error);
      showErrorAlert('Error', `Error al intentar editar el usuario seleccionado.`);
    }
  };

  const handleValuesInEditMode = (event) => {
    setValuesInEditMode({
      ...valuesInEditMode,
      [event.target.name]: event.target.value,
    });
  };

  const handleUpdateUser = async (id) => {
    try {
      const data = {
        alias: valuesInEditMode.alias,
        phone: valuesInEditMode.phone,
      };

      await updateUser(id, data);

      cancelEditMode();
      showSuccessAlert('Actualización exitosa', 'El usuario ha sido actualizado.');
      onUserSave();
    } catch (error) {
      console.error('Error', error);
      showErrorAlert('Error', error.message);
    }
  };

  const cancelEditMode = () => {
    setRowInEditMode(null);
    setValuesInEditMode({});
  };

  const handleDeactivateUser = async (id, shouldDeactivateCards) => {
    try {
      await deactivateUser(id, shouldDeactivateCards);
      showSuccessAlert('Desactivación exitosa', 'El usuario ha sido desactivado.');
      onUserSave();
    } catch (error) {
      console.error('Error', error);
      showErrorAlert('Error', error.message);
    }
  };

  const handleActivateUser = async (id, shouldActivateCards) => {
    try {
      await activateUser(id, shouldActivateCards);
      showSuccessAlert('Activación exitosa', 'El usuario ha sido activado.');
      onUserSave();
    } catch (error) {
      console.error('Error', error);
      showErrorAlert('Error', error.message);
    }
  };

  return (
    <div className="mt-3 p-0 table-users">
      <table className="table table-striped">
        <thead className="text-center font-weight-bold">
          <tr>
            <th scope="col">Foto</th>
            <th scope="col">Nombre completo</th>
            <th scope="col">Documento</th>
            <th scope="col">Activo?</th>
            <th scope="col">Verificado?</th>
            <th scope="col">Email</th>
            <th scope="col">Teléfono</th>
            <th scope="col">Rol</th>
            <th scope="col">Tags</th>
            <th scope="col">Apto.</th>
            <th scope="col">Edificio</th>
            <th scope="col">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {users.map((item) => {
            const { user } = item;
            const { id, photo, name, cedula, phone, email, active, verified, roleName, apartmentName, buildingName } =
              user;

            return (
              <tr key={id}>
                {rowInEditMode === id ? (
                  <>
                    <td data-label="Foto" className="align-middle">
                      <button type="button" className="btn" onClick={() => openPhotoModal(photo)}>
                        Ver foto
                      </button>
                    </td>
                    <td data-label="Nombre" className="align-middle">
                      {name}
                    </td>
                    <td data-label="Documento" className="align-middle">
                      {cedula}
                    </td>
                    <td data-label="Activo?" className="align-middle">
                      <span className="material-icons">{active ? 'check' : 'close'}</span>
                    </td>
                    <td data-label="Verificado?" className="align-middle">
                      <span className="material-icons">{verified ? 'check' : 'close'}</span>
                    </td>
                    <td data-label="Email" className="align-middle">
                      {email}
                    </td>
                    <td data-label="Teléfono" className="align-middle">
                      <input
                        type="tel"
                        name="phone"
                        value={valuesInEditMode.phone}
                        onChange={handleValuesInEditMode}
                        className="form-control text-center w-100 h-100"
                        required
                      />
                    </td>
                    <td data-label="Rol" className="align-middle">
                      {roleName}
                    </td>
                    <td data-label="Tags" className="align-middle text-left">
                      {user.tags && user.tags.length > 0 ? (
                        <span>
                          {user.tags.map((tagObj, index) => (
                            <span key={index}>
                              {tagObj.tag} ({tagObj.type}) {index < user.tags.length - 1 && <br />}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span>Sin tags</span>
                      )}
                    </td>
                    <td data-label="Apto." className="align-middle">
                      {apartmentName}
                    </td>
                    <td data-label="Edificio" className="align-middle">
                      {buildingName}
                    </td>
                    <td data-label="Acciones" className="align-middle">
                      <button
                        type="button"
                        className="btn btn-success p-1 mr-2"
                        onClick={() => {
                          showConfirmAlert({
                            title: 'Advertencia',
                            message: `¡Atención! Va a actualizar la información del usuario  ${cedula}. ¿Está seguro de continuar?`,
                            onConfirm: () => {
                              handleUpdateUser(id);
                            },
                          });
                        }}
                      >
                        <span className="material-icons d-flex align-items-center">check</span>
                      </button>
                      <button type="button" className="btn btn-danger p-1" onClick={cancelEditMode}>
                        <span className="material-icons d-flex align-items-center">close</span>
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label="Foto" className="align-middle">
                      <button type="button" className="btn" onClick={() => openPhotoModal(photo)}>
                        Ver foto
                      </button>
                    </td>
                    <td data-label="Nombre" className="align-middle">
                      {name}
                    </td>
                    <td data-label="Documento" className="align-middle">
                      {cedula}
                    </td>
                    <td data-label="Activo?" className="align-middle">
                      <span className="material-icons">{active ? 'check' : 'close'}</span>
                    </td>
                    <td data-label="Verificado?" className="align-middle">
                      <span className="material-icons">{verified ? 'check' : 'close'}</span>
                    </td>
                    <td data-label="Email" className="align-middle">
                      {email}
                    </td>
                    <td data-label="Teléfono" className="align-middle">
                      {phone}
                    </td>
                    <td data-label="Rol" className="align-middle">
                      {roleName}
                    </td>
                    <td data-label="Tags" className="align-middle text-left">
                      {user.tags && user.tags.length > 0 ? (
                        <span>
                          {user.tags.map((tagObj, index) => (
                            <span key={index}>
                              {tagObj.tag} ({tagObj.type}){index < user.tags.length - 1 && <br />}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span>Sin tags</span>
                      )}
                    </td>
                    <td data-label="Apto." className="align-middle">
                      {apartmentName}
                    </td>
                    <td data-label="Edificio" className="align-middle">
                      {buildingName}
                    </td>
                    <td data-label="Acciones" className="align-middle">
                      <button type="button" className="btn p-1 mr-2" onClick={() => enterEditMode(id)}>
                        <span className="material-icons d-flex align-items-center">edit</span>
                      </button>
                      <button
                        type="checkbox"
                        className="btn p-1"
                        style={{
                          backgroundColor: active
                            ? 'rgb(34 197 94 / var(--tw-bg-opacity, 1))'
                            : 'rgb(220 38 38 / var(--tw-bg-opacity, 1))',
                        }}
                        onClick={() => {
                          showConfirmAlertWithCheckbox({
                            title: 'Advertencia',
                            messageCheckbox: active
                              ? `Desactivar las tarjetas asociadas al usuario.`
                              : `Activar las tarjetas asociadas al usuario.`,
                            message: active
                              ? `¡Atención! Vas a desactivar al usuario ${cedula}. ¿Estás seguro de continuar?`
                              : `¡Atención! Vas a activar al usuario ${cedula}. ¿Estás seguro de continuar?`,
                            onConfirm: (inputValue) => {
                              active
                                ? handleDeactivateUser(id, { shouldDeactivateCards: inputValue })
                                : handleActivateUser(id, { shouldActivateCards: inputValue });
                            },
                          });
                        }}
                      >
                        <span className="material-icons d-flex align-items-center" style={{ color: 'white' }}>
                          {active ? 'toggle_on' : 'toggle_off'}
                        </span>
                      </button>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal
        open={showPhotoModal}
        onClose={closePhotoModal}
        center
        classNames={{ overlay: 'background-modal', modal: 'customModal' }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: '#00bfdf',
            borderRadius: '15px',
            padding: '20px',
            margin: '15px',
            maxWidth: '200px',
            maxHeight: '300px',
            boxSizing: 'border-box',
          }}
        >
          <button type="button" className="button-close" onClick={closePhotoModal}>
            <span aria-hidden="true" className="button-span">
              &times;
            </span>
          </button>

          {selectedPhoto ? (
            <img
              src={selectedPhoto}
              alt="Foto del residente"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                objectFit: 'contain',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                marginTop: '20px',
              }}
            />
          ) : (
            <span
              style={{
                color: 'white',
                marginTop: '40px',
              }}
            >
              No hay foto disponible
            </span>
          )}
        </div>
      </Modal>
    </div>
  );
};

UsersList.propTypes = {
  users: PropTypes.array,
  onUserSave: PropTypes.func,
};

export default UsersList;
